/**
 * GradingV2 - Modern Grading Page
 *
 * V2 implementation of the Grading page with:
 * - Split pane layout (code on left, rubric on right)
 * - Reuses existing ShowCodeReadOnly/CodeEditor components
 * - Uses RubricViewV2 for grading interface
 */
import React, { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { Breadcrumb } from 'react-bootstrap'
import SplitPane from 'react-split-pane'
import JSZip from 'jszip'
import * as constants from 'Constants'
import httpService from 'services/httpService'
import ShowCodeReadOnly from '../../../Programming/Components/ShowCodeReadOnly/ShowCodeReadOnly'
import CodeEditor from '../../../Programming/Components/CodeEditor/CodeEditor'
import RubricViewV2 from '../RubricViewV2'
import '../../../Programming/Components/SubjectiveGrading/splitter.scss'

const GradingV2 = ({ submissionData, setSubmissionData }) => {
  const { courseId, labId, activityId } = useParams()
  const location = useLocation()

  // Lab and activity titles
  const [labTitle, setLabTitle] = useState('')
  const [activityTitle, setActivityTitle] = useState('')

  // Submission state
  const [submissionPath, setSubmissionPath] = useState(
    location.state?.submission?.file_submitted || ''
  )
  const [submission, setSubmission] = useState(location.state?.submission)
  const [numberOfSubmissions, setNumberOfSubmissions] = useState(submissionData?.length)
  const [nextSubmissionURL, setNextSubmissionURL] = useState(
    location.state?.nextSubmissionURL || null
  )
  const numOfRows = location.state?.numOfRows || 100

  // File content state
  const [fileContent, setFileContent] = useState([])
  const [isEditEnabled, setIsEditEnabled] = useState(false)

  // Handle ResizeObserver error
  useEffect(() => {
    const errorHandler = (e) => {
      if (
        e.message.includes(
          'ResizeObserver loop completed with undelivered notifications' ||
            'ResizeObserver loop limit exceeded'
        )
      ) {
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        )
        if (resizeObserverErr) {
          resizeObserverErr.style.display = 'none'
        }
      }
    }
    window.addEventListener('error', errorHandler)
    return () => {
      window.removeEventListener('error', errorHandler)
    }
  }, [])

  // Store/restore submission data
  useEffect(() => {
    if (submissionData !== undefined && submissionData !== null) {
      localStorage.setItem('submissionData', JSON.stringify(submissionData))
      setNumberOfSubmissions(submissionData?.length)
      setSubmissionData(submissionData)
    } else if (submissionData === undefined || submissionData === null) {
      const stored = localStorage.getItem('submissionData')
      if (stored !== null && stored !== undefined) {
        const parsedData = JSON.parse(stored)
        setNumberOfSubmissions(parsedData?.length)
        setSubmissionData(parsedData)
      }
    }
  }, [submissionData, activityTitle, labTitle])

  // Fetch lab and activity details
  useEffect(() => {
    const fetchLabDetails = async () => {
      if (labId) {
        const url = `${constants.base_url}/lab/all/${labId}?courseId=${courseId}`
        try {
          const response = await httpService.get(url)
          setLabTitle(response.data.lab.title)
          const activity = response.data.activities.find(
            (activity) => activity.id == activityId
          )
          setActivityTitle(activity?.name || '')
        } catch (error) {
          console.error(error)
        }
      }
    }

    fetchLabDetails()
  }, [labId, courseId, activityId])

  // Read file content
  useEffect(() => {
    const readFile = async () => {
      try {
        if (submissionPath) {
          const url = `${constants.base_url}/${submissionPath.toString().substr(1)}`
          const response = await fetch(url)

          if (!response.ok) {
            throw new Error('Network response was not ok')
          }

          const contentType = response.headers.get('Content-Type')

          // Handle ZIP files
          if (
            contentType === 'application/zip' ||
            contentType === 'application/octet-stream' ||
            contentType === 'application/x-zip-compressed'
          ) {
            const blob = await response.blob()
            const zip = new JSZip()
            const zipContent = await zip.loadAsync(blob)
            const contents = []

            for (const fileName in zipContent.files) {
              const file = zipContent.files[fileName]
              if (!file.dir) {
                const text = await file.async('text')
                contents.push({ name: fileName, content: text })
              }
            }

            setFileContent(contents)
          } else if (
            contentType === 'text/plain' ||
            contentType === 'text/x-c++src' ||
            contentType === 'text/x-python' ||
            contentType === 'text/x-java' ||
            contentType === 'text/x-c' ||
            contentType === 'text/x-csharp' ||
            contentType === 'text/html; charset=utf-8' ||
            contentType === 'text/x-csrc'
          ) {
            const text = await response.text()
            const singleFileContent = [
              { name: submissionPath.split('/').pop(), content: text },
            ]
            setFileContent(singleFileContent)
          } else {
            throw new Error('Unsupported file type: ' + contentType)
          }
        } else {
          setFileContent([])
        }
      } catch (error) {
        console.error('Error occurred while reading the file:', error)
        setFileContent([])
      }
    }

    readFile()
  }, [submissionPath])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
        position: 'relative',
        overflow: 'auto',
      }}
    >
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-container">
        <Breadcrumb>
          <Breadcrumb.Item href={`/courseware/course/${courseId}/content/labs`}>
            Labs
          </Breadcrumb.Item>
          <Breadcrumb.Item
            href={`/courseware/course/${courseId}/content/labs/${labId}/`}
          >
            {labTitle}
          </Breadcrumb.Item>
          <Breadcrumb.Item
            href={`/courseware/course/${courseId}/content/labs/${labId}/programming/${activityId}`}
          >
            {activityTitle}
          </Breadcrumb.Item>
          <Breadcrumb.Item
            href={`/courseware/course/${courseId}/content/labs/${labId}/programming/${activityId}/submissions`}
          >
            Submissions
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Grading</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      {/* Split Pane Layout */}
      <SplitPane
        split="vertical"
        defaultSize="50%"
        style={{
          position: 'static',
          height: '100vh',
        }}
      >
        {/* Left Pane - Code Display */}
        <div
          className="flex-1 overflow-y-auto border-r-2 p-0"
          style={{ height: '100vh' }}
        >
          {!isEditEnabled ? (
            <ShowCodeReadOnly
              fileContent={fileContent}
              submissionPath={submissionPath}
              isEditEnabled={isEditEnabled}
              setIsEditEnabled={setIsEditEnabled}
              activityTitle={activityTitle}
            />
          ) : (
            <div style={{ minHeight: 'calc(100vh - 100px)', minWidth: '10%' }}>
              <CodeEditor
                programId={activityId}
                submission={submission}
                fileContent={fileContent}
                submissionPath={submissionPath}
                isEditEnabled={isEditEnabled}
                setIsEditEnabled={setIsEditEnabled}
                activityTitle={activityTitle}
              />
            </div>
          )}
        </div>

        {/* Right Pane - Rubric View */}
        <div
          className="relative flex-1 p-0"
          style={{ height: '100vh', overflowY: 'auto', overflowX: 'visible' }}
        >
          {submission ? (
            <RubricViewV2
              programId={activityId}
              submission={submission}
              readOnly={false}
              submissionData={submissionData}
              setSubmissionData={setSubmissionData}
              setSubmissionPath={setSubmissionPath}
              setSubmission={setSubmission}
              nextSubmissionURL={nextSubmissionURL}
              setNextSubmissionURL={setNextSubmissionURL}
              numOfRows={numOfRows}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: '#6b7280',
              }}
            >
              Submission Not Found
            </div>
          )}
        </div>
      </SplitPane>
    </div>
  )
}

export default GradingV2
